import React, { useState } from 'react';
import { Settings, Users, Shield, Database, ScrollText, FileText, Lock, UserCheck, Search, ArrowLeft, Warehouse, Globe, Stethoscope, Upload, Printer, Image, ShieldAlert } from 'lucide-react';

import { DataInspection } from './DataInspection';
import { DataIntegrity } from './DataIntegrity';
import { WarehouseMaintenance } from './WarehouseMaintenance';
import TranslationAdmin from './TranslationAdmin';
import { PutAwayDiagnostics } from './PutAwayDiagnostics';
import LogViewer from '../application-logs/LogViewer';
import SystemSettings from './SystemSettings';
import { InventoryImport } from './InventoryImport';
import { PrintHistory } from './PrintHistory';
import { MediaLibrary } from './MediaLibrary';

interface AdministrationProps {
  onBack?: () => void;
  onModuleChange?: (moduleId: string) => void;
}

export const Administration: React.FC<AdministrationProps> = ({ onBack, onModuleChange }) => {
    const [currentView, setCurrentView] = useState<string>('overview');
    const [selectedModule, setSelectedModule] = useState<string>('');

    const adminModules = [
        { id: 'system-settings', name: 'Systeminnstillinger', icon: Settings, description: 'Globale systemkonfigurasjoner' },
        { id: 'warehouse-maintenance', name: 'Vedlikehold Lager', icon: Warehouse, description: 'Vedlikehold av lagergrunndata' },
        { id: 'data-inspection', name: 'Grunndata-inspeksjon', icon: Search, description: 'Inspiser og analyser grunndata' },
        { id: 'data-integrity', name: 'Dataintegritet', icon: ShieldAlert, description: 'Analyser datakvalitet og relasjonsintegritet' },
        { id: 'putaway-diagnostics', name: 'Put-Away Diagnostikk', icon: Stethoscope, description: 'Diagnostiser og feilsøk vareplassering' },
        { id: 'translation-admin', name: 'Oversettelser', icon: Globe, description: 'Administrer språk og oversettelser' },
        { id: 'system-logs', name: 'Systemlogger', icon: ScrollText, description: 'Se systemlogger og applikasjonsaktivitet' },
        { id: 'operator-profiles', name: 'Operatørprofiler', icon: UserCheck, description: 'Administrer operatørprofiler og tilganger' },
        { id: 'inventory-import', name: 'Saldoimport', icon: Upload, description: 'Importer lokasjoner og startsaldo fra CSV' },
           { id: 'print-history', name: 'Utskriftshistorikk', icon: Printer, description: 'Se utskriftshistorikk og skriv ut på nytt' },
        { id: 'media-library', name: 'Mediebibliotek', icon: Image, description: 'Bilder, signaturer og vedlegg' }
    ];

    const handleModuleClick = (moduleId: string) => {
        if (moduleId === 'data-inspection') {
            setCurrentView('data-inspection');
            setSelectedModule(moduleId);
        } else if (moduleId === 'warehouse-maintenance') {
            setCurrentView('warehouse-maintenance');
            setSelectedModule(moduleId);
        } else if (moduleId === 'translation-admin') {
            setCurrentView('translation-admin');
            setSelectedModule(moduleId);
        } else if (moduleId === 'data-integrity') {
            setCurrentView('data-integrity');
            setSelectedModule(moduleId);
        } else if (moduleId === 'putaway-diagnostics') {
            setCurrentView('putaway-diagnostics');
            setSelectedModule(moduleId);
        } else if (moduleId === 'system-logs') {
            setCurrentView('system-logs');
            setSelectedModule(moduleId);
        } else if (moduleId === 'system-settings') {
            setCurrentView('system-settings');
            setSelectedModule(moduleId);
        } else if (moduleId === 'inventory-import') {
            setCurrentView('inventory-import');
            setSelectedModule(moduleId);
        } else if (moduleId === 'print-history') {
            setCurrentView('print-history');
            setSelectedModule(moduleId);
        } else if (moduleId === 'media-library') {
            setCurrentView('media-library');
            setSelectedModule(moduleId);
        } else if (moduleId === 'operator-profiles') {
            // Naviger til Profiladministrasjon-modulen
            if (onModuleChange) {
                onModuleChange('profile-admin');
            }
        }
        else {
            // Placeholder for andre moduler
            console.log('Module clicked:', moduleId);
        }
    };

    const handleBackToOverview = () => {
        setCurrentView('overview');
        setSelectedModule('');
    };

    if (currentView === 'data-inspection') {
        return (
            <div className="space-y-6">
                {/* Back Button */}
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleBackToOverview}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Tilbake til administrasjon
                    </button>
                </div>
                
                {/* Data Inspection Component */}
                <DataInspection />
            </div>
        );
    }

    if (currentView === 'warehouse-maintenance') {
        return (
            <div className="space-y-6">
                {/* Back Button */}
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleBackToOverview}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Tilbake til administrasjon
                    </button>
                </div>
                
                {/* Warehouse Maintenance Component */}
                <WarehouseMaintenance />
            </div>
        );
    }

    if (currentView === 'translation-admin') {
        return (
            <div className="space-y-6">
                {/* Back Button */}
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleBackToOverview}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Tilbake til administrasjon
                    </button>
                </div>

                {/* Translation Admin Component */}
                <TranslationAdmin />
            </div>
        );
    }

    if (currentView === 'system-logs') {
        return (
            <div className="space-y-6">
                {/* Back Button */}
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleBackToOverview}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Tilbake til administrasjon
                    </button>
                </div>

                {/* System Logs Component */}
                <LogViewer />
            </div>
        );
    }

    if (currentView === 'data-integrity') {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleBackToOverview}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Tilbake til administrasjon
                    </button>
                </div>
                <DataIntegrity />
            </div>
        );
    }

    if (currentView === 'putaway-diagnostics') {
        return <PutAwayDiagnostics onBack={handleBackToOverview} />;
    }

    if (currentView === 'system-settings') {
        return (
            <div className="space-y-6">
                {/* Back Button */}
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleBackToOverview}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Tilbake til administrasjon
                    </button>
                </div>

                {/* System Settings Component */}
                <SystemSettings />
            </div>
        );
    }

    if (currentView === 'inventory-import') {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleBackToOverview}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Tilbake til administrasjon
                    </button>
                </div>
                <InventoryImport />
            </div>
        );
    }

    if (currentView === 'print-history') {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleBackToOverview}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Tilbake til administrasjon
                    </button>
                </div>
                <PrintHistory />
            </div>
        );
    }

    if (currentView === 'media-library') {
        return (
            <div className="space-y-6">
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleBackToOverview}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Tilbake til administrasjon
                    </button>
                </div>
                <MediaLibrary />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 text-white p-6 rounded-lg">
                <div className="flex items-center gap-3 mb-2">
                    <Settings className="w-8 h-8" />
                    <h1 className="text-2xl font-bold">Administrasjon</h1>
                </div>
                <p className="text-slate-200">Administrer systemet og brukere</p>
            </div>

            {/* Module Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {adminModules.map(module => {
                    const Icon = module.icon;
                    return (
                        <div 
                            key={module.id}
                            className="bg-white rounded-lg p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow cursor-pointer group"
                            onClick={() => handleModuleClick(module.id)}
                        >
                            <div className="flex items-start gap-4">
                                <div className="bg-blue-100 p-3 rounded-lg group-hover:bg-blue-200 transition-colors">
                                    <Icon className="w-6 h-6 text-blue-600" />
                                </div>
                                <div className="flex-1">
                                    <h3 className="font-semibold text-gray-900 mb-1">{module.name}</h3>
                                    <p className="text-sm text-gray-600">{module.description}</p>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

        </div>
    );
};